import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";
import { SpacingVariants } from "../../../types/spacing";
import classes from "./grid.module.css";

export type GridContainerBaseProps = HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  gap?: SpacingVariants;
  rowGap?: SpacingVariants;
  columnGap?: SpacingVariants;
};

type GridContainerProps = GridContainerBaseProps &
  (
    | {
        columns: number;
        minmax?: never;
      }
    | {
        columns?: never;
        minmax: number;
      }
  );

export const Container = forwardRef<HTMLDivElement, GridContainerProps>(
  (
    {
      asChild,
      className,
      minmax,
      columns,
      gap,
      rowGap,
      columnGap,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "div";

    const inlineStyle = {
      "--columns": columns,
      "--minmax": minmax,
      ...(gap ? { gap: `var(--spacing-${gap})` } : null),
      ...(rowGap ? { rowGap: `var(--spacing-${rowGap})` } : null),
      ...(columnGap ? { columnGap: `var(--spacing-${columnGap})` } : null),
      ...style,
    };

    return (
      <Comp
        ref={ref as React.LegacyRef<HTMLParagraphElement>}
        className={clsx(classes.container, className, {
          [classes.columns as string]: columns,
        })}
        style={inlineStyle}
        {...props}
      />
    );
  },
);

Container.displayName = "GridContainer";

export type GridItemProps = HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  span?: number;
  justify?: "start" | "center" | "end";
};

export const Item = forwardRef<HTMLDivElement, GridItemProps>(
  ({ asChild, className, span, justify, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        className={clsx(justify && classes[`justify-${justify}`], className, {
          [classes.span as string]: span,
        })}
        style={{ "--span": span, ...style } as React.CSSProperties}
        {...props}
      />
    );
  },
);

Item.displayName = "GridItem";
